/*
 * feed-video.js — single-active video playback on mobile for shop-the-feed cards.
 *
 * <video-media> extends DeferredMedia, which lazy-mounts <video> from a
 * <template> via Motion.inView when [autoplay] is set. That fires play()
 * on every card as it enters the viewport — so with several cards visible
 * at once on a phone, multiple HD videos start loading in parallel, and
 * the "active" card ends up being whichever entered the viewport last
 * (i.e. the one below the centred card), not the one the user is looking at.
 *
 * This script:
 *   1. Tracks intersectionRatio for every video-media[data-feed-video] via
 *      an IntersectionObserver with multi-step thresholds.
 *   2. On any change, picks the card with the highest ratio ("the one
 *      most centred on screen") and ensures only that card is playing —
 *      pauses everything else.
 *   3. Intercepts the `video:play` events that DeferredMedia fires on
 *      its own from Motion.inView, and pauses any video that isn't the
 *      current focus — otherwise the last-to-enter card always wins.
 *
 * Desktop hover behavior (via <secondary-video>) is untouched: that path
 * doesn't apply on touch, and on desktop we just track activeMedia
 * without forcing pause.
 *
 * Also pauses the active card when the tab is hidden — saves battery/data.
 */

(function () {
  'use strict';

  const MOBILE_MQ = '(max-width: 1023px)';
  const SELECTOR = 'video-media[data-feed-video]';
  // Below this ratio we don't bother autoplaying anything (card is mostly off-screen).
  const MIN_VISIBLE_RATIO = 0.4;

  let activeMedia = null;
  const ratios = new Map();

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function isFeedMedia(target) {
    return target && target.matches && target.matches(SELECTOR);
  }

  function pickFocus() {
    let bestRatio = 0;
    let best = null;
    for (const [media, ratio] of ratios) {
      if (ratio > bestRatio) {
        bestRatio = ratio;
        best = media;
      }
    }
    return { best, bestRatio };
  }

  function applyFocus() {
    if (!isMobile()) return;
    const { best, bestRatio } = pickFocus();
    if (best === activeMedia) return;
    if (activeMedia && activeMedia !== best) {
      try { activeMedia.pause(); } catch (e) { /* ignore */ }
    }
    activeMedia = best;
    if (best && bestRatio >= MIN_VISIBLE_RATIO && !best.hasAttribute('playing')) {
      try { best.play(); } catch (e) { /* ignore */ }
    }
  }

  function pauseAllExcept(keep) {
    for (const media of ratios.keys()) {
      if (media !== keep && media.hasAttribute('playing')) {
        try { media.pause(); } catch (e) { /* ignore */ }
      }
    }
  }

  function initObserver() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) ratios.set(e.target, e.intersectionRatio);
      applyFocus();
    }, {
      threshold: [0, 0.25, 0.5, 0.75, 1.0],
    });
    document.querySelectorAll(SELECTOR).forEach(media => io.observe(media));
  }

  // On mobile, override Motion.inView's "play everything in viewport" behaviour:
  // when a video starts playing, check whether it's actually the centred card.
  // If not, pause it and hand play to the real focus.
  document.addEventListener('video:play', (event) => {
    const target = event.target;
    if (!isFeedMedia(target)) return;

    if (!isMobile()) {
      // Desktop: hover-driven via <secondary-video>, no enforcement.
      activeMedia = target;
      return;
    }

    const { best, bestRatio } = pickFocus();
    const targetRatio = ratios.get(target) || 0;

    if (best && best !== target && bestRatio > targetRatio) {
      // This isn't the centred card — pause it, hand play to the focus.
      try { target.pause(); } catch (e) { /* ignore */ }
      if (!best.hasAttribute('playing')) {
        try { best.play(); } catch (e) { /* ignore */ }
      }
      activeMedia = best;
    } else {
      // Either this IS the focus, or we don't have a better candidate yet.
      pauseAllExcept(target);
      activeMedia = target;
    }
  }, { passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden && activeMedia) {
      try { activeMedia.pause(); } catch (e) { /* ignore */ }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initObserver);
  } else {
    initObserver();
  }
})();
